import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import MessagePopup from "./MessagePopup";
import apiEndpoints from "../../apiconfig";
import { useSnackbar } from "../../DynamicComponents/SnackbarContext";

export default function SingleMsg() {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [messageContent, setMessageContent] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateVariables, setTemplateVariables] = useState([]);
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);


  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSelectTemplate = (template) => {
    setMessageContent(template.templateBody || template.content || "");
    const templateJson = JSON.parse(template.template_json);

    let headerImage = null;
    if (
      templateJson.components &&
      templateJson.components[0].format === "IMAGE" &&
      templateJson.components[0].example &&
      templateJson.components[0].example.header_handle
    ) {
      headerImage = templateJson.components[0].example.header_handle[0];
    }

    setSelectedTemplate({
      ...template,
      header: headerImage
        ? { type: "image", link: headerImage }
        : null
    });
    setIsPopupOpen(false);

    const matches = (template.templateBody || "").match(/{{\d+}}/g) || [];
    const variableFields = matches.map((match) => ({
      placeholder: match,
      value: "",
      type: "text",
    }));
    setTemplateVariables(variableFields);

    if (errors.messageContent) {
      setErrors((prev) => ({ ...prev, messageContent: "" }));
    }
  };

  const handleClear = () => {
    setMessageContent("");
    setMobileNumber("");
    setCountryCode("");
    setSelectedTemplate(null);
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    if (!countryCode) newErrors.countryCode = "Country code is required";
    if (!mobileNumber) newErrors.mobileNumber = "Mobile number is required";
    else if (mobileNumber.length !== 10)
      newErrors.mobileNumber = "Mobile number must be 10 digits";
    if (!messageContent)
      newErrors.messageContent = "Message content is required";
    return Object.keys(newErrors).length === 0;
  };

  const sendWhatsAppMessage = async () => {
    try {
      const fullPhoneNumber = countryCode + mobileNumber;
      if (!selectedTemplate) {
        showSnackbar("Please select a template before sending.", "warning");
        return;
      }

      const response = await fetch(apiEndpoints.sendmessage, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`
        },
        body: JSON.stringify({
          to: fullPhoneNumber,
          template_name: selectedTemplate.name,
          template_id: selectedTemplate.id,
          language: selectedTemplate.languageName || "en",
          variables: templateVariables.map((v) => v.value),
          header: selectedTemplate.header || null,
          buttons: selectedTemplate.buttons || [],
          message_content: messageContent,
        }),
      });

      const result = await response.json();
      if (result.status === "success") {
        showSnackbar("Message sent & stored successfully!", "success");
        handleClear();
      } else if (result.status === "partial") {
        showSnackbar(
          "Some contacts were blocked. Message sent partially.",
          "warning"
        );
      } else {
        showSnackbar(result.message || "This contact is blocked", "error");
      }
    } catch (error) {
      showSnackbar("Something went wrong while sending the message.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!validateForm()) return;
    if (templateVariables.some((v) => !v.value.trim())) {
      showSnackbar("Please fill in all variable values before sending.", "error");
      return;
    }
    setLoading(true);
    await sendWhatsAppMessage();
  };

  return (
    <div
      className="w-full bg-[#F4F5F7] p-4 sm:p-6"
      style={{ fontFamily: "'Montserrat', sans-serif" }}
    >
      {/* Responsive Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Section */}
        <div className="bg-white p-5 sm:p-6 rounded-2xl shadow-md border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Recipient Details
          </h3>

          <div className="space-y-5">
            {/* Country Code */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">
                Country Code<span className="text-red-500">*</span>
              </label>
              <select
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#25D366] focus:outline-none ${errors.countryCode ? "border-red-500" : "border-gray-300"
                  }`}
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
              >
                <option value="">Select Country</option>
                <option value="+91">India (+91)</option>
                <option value="+1">USA (+1)</option>
                <option value="+44">UK (+44)</option>
                <option value="+971">UAE (+971)</option>
              </select>
              {errors.countryCode && (
                <p className="text-red-500 text-xs mt-1">{errors.countryCode}</p>
              )}
            </div>

            {/* Mobile */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">
                Mobile Number<span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={mobileNumber}
                  onChange={(e) => {
                    const onlyNums = e.target.value.replace(/[^0-9]/g, "");
                    setMobileNumber(onlyNums);
                  }}
                  maxLength="10"
                  placeholder="Enter 10-digit number"
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#25D366] focus:outline-none ${errors.mobileNumber ? "border-red-500" : "border-gray-300"
                    }`}
                />
                {mobileNumber && (
                  <button
                    onClick={() => setMobileNumber("")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
              {errors.mobileNumber ? (
                <p className="text-red-500 text-xs mt-1">
                  {errors.mobileNumber}
                </p>
              ) : (
                <p className="text-gray-400 text-xs mt-1">
                  Only numbers allowed (10 digits)
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Right Section */}
        <div className="bg-white p-5 sm:p-6 rounded-2xl shadow-md border border-gray-100 flex flex-col justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Message Composer
            </h3>

            {/* Message Content */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Message Content<span className="text-red-500">*</span>
                </label>
                {messageContent && (
                  <button
                    onClick={() => setMessageContent("")}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              <textarea
                readOnly
                onClick={() => setIsPopupOpen(true)}
                value={messageContent}
                placeholder="Click here to select a WhatsApp template"
                className={`w-full p-3 border rounded-lg h-40 bg-gray-50 cursor-pointer focus:ring-2 focus:ring-[#25D366] ${errors.messageContent ? "border-red-500" : "border-gray-300"
                  }`}
              />
            </div>

            {/* Template Variables */}
            {selectedTemplate && templateVariables.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Template Variables
                </h4>
                {templateVariables.map((variable, index) => (
                  <div
                    key={index}
                    className="flex flex-wrap sm:flex-nowrap items-center gap-2 border border-gray-200 p-2 rounded-lg bg-gray-50 mb-2"
                  >
                    <span className="font-mono text-gray-700 text-sm sm:w-16 w-auto sm:text-left text-center">
                      {variable.placeholder}
                    </span>
                    <input
                      type="text"
                      placeholder="Enter sample value"
                      value={variable.value}
                      onChange={(e) => {
                        const updated = [...templateVariables];
                        updated[index].value = e.target.value;
                        setTemplateVariables(updated);
                      }}
                      className="flex-1 w-full border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-[#25D366] focus:outline-none"
                    />
                  </div>

                ))}
              </div>
            )}
          </div>

          {/* ✅ Action Buttons - stay inside grid on mobile */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 mt-4">
            <button
              onClick={handleClear}
              className="w-full sm:w-auto px-5 py-2 bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-100 transition"
              disabled={loading}
            >
              Clear
            </button>
            <button
              onClick={() => handleSendMessage(false)}
              className="w-full sm:w-auto px-6 py-2 flex items-center justify-center bg-[#25D366] text-white rounded-lg hover:bg-[#1CB85C] transition disabled:bg-gray-400"
              disabled={
                !countryCode || !mobileNumber || !messageContent || loading
              }
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>Send Message</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Template Popup */}
      {isPopupOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-auto">
          <div className="w-full flex justify-center items-start mt-10 md:items-center">
            <MessagePopup
              onClose={() => setIsPopupOpen(false)}
              onSelectTemplate={handleSelectTemplate}
            />
          </div>
        </div>
      )}
    </div>
  );
}
